import axios from 'axios';
import { apiHost } from './apiHost';
import { idPerangkat } from './perangkat';
import { alamatLayar, buatPenahanSelesai } from './layarPelanggan';

// Layar pelanggan kasir web: SATU halaman layar untuk web & Flutter (/layar di server). Kasir membuat sesi di server (kode 4 angka),
// membuka /layar?kode=... di jendela baru, lalu mengirim keadaan terbaru (belanjaan / QRIS / selesai) tiap berubah.
// Logika pengiriman sama dengan aplikasi Flutter (lib/layar_pelanggan/layar_pelanggan_kasir.dart): ditunda sebentar (debounce),
// satu kiriman pada satu waktu, keadaan terbaru kasir selalu menang, sesi dibuat ulang bila server restart (404) / akun berganti (403).
// Gagal kirim tidak pernah mengganggu kasir.

export class PengirimLayar {
  /** panggil(metode, path, data) -> status HTTP (angka; 0 = server tidak terjangkau) dan data. */
  constructor({ panggil, jeda = 250, otomatis = null, jedaOtomatis = 5000 }) {
    this.panggil = panggil;
    this.jeda = jeda;
    this.otomatis = otomatis; // { ambil(): bool, simpan(bool) }: layar pelanggan dipakai? (bertahan setelah halaman dimuat ulang)
    this.jedaOtomatis = jedaOtomatis;
    this.percobaanOtomatis = 0;
    this.kode = null;
    this.token = null; // token sesi (untuk tautan pemasangan /layar?kode=...&token=...)
    this.saatKodeBerubah = null; // (kode|null) => void
    this.saring = buatPenahanSelesai();
    this.diinginkan = null; // pesan terbaru dari kasir (walau belum ada sesi)
    this.terakhirDikirim = null;
    this.tertunda = null;
    this.mengirim = false;
    this.timer = null;
    this.kirim = this.kirim.bind(this);
  }

  get aktif() {
    return this.kode !== null;
  }

  ubahKode(kode) {
    this.kode = kode;
    if (this.saatKodeBerubah) this.saatKodeBerubah(kode);
  }

  /** Buat (atau ambil kembali) sesi di server. Mengembalikan kode, atau null bila gagal. */
  async mulai() {
    const { status, data } = await this.panggil('POST', '/layar-pelanggan/sesi');
    const kode = status === 200 && data && data.kode ? String(data.kode) : null;
    if (!kode) return null;
    this.token = data.token ? String(data.token) : null; // sebelum kode: pendengar kode langsung punya tokennya
    this.terakhirDikirim = null;
    if (this.otomatis) this.otomatis.simpan(true);
    this.ubahKode(kode);
    if (this.diinginkan) this.antri(this.diinginkan, true);
    return kode;
  }

  /** Akhiri sesi: kode tidak berlaku lagi. */
  async hentikan() {
    const ada = this.aktif;
    clearTimeout(this.timer);
    this.tertunda = null;
    this.terakhirDikirim = null;
    if (this.otomatis) this.otomatis.simpan(false);
    this.token = null;
    this.ubahKode(null);
    if (ada) await this.panggil('DELETE', '/layar-pelanggan/sesi');
  }

  /** Dipanggil kasir tiap isi keranjang / pembayaran berubah. Aman dipanggil kapan saja (tanpa sesi = hanya diingat). */
  kirim(pesan) {
    const lolos = this.saring(pesan);
    if (!lolos) return;
    this.diinginkan = lolos;
    if (this.aktif) { this.antri(lolos, lolos.tipe === 'selesai' || lolos.tipe === 'qris'); return; }
    this.pulihkanSesi();
  }

  /** Halaman dimuat ulang saat layar pelanggan dipakai: sesi dilanjutkan sendiri (akun yang sama dapat kode yang sama). */
  pulihkanSesi(sekarang = Date.now()) {
    if (!this.otomatis || !this.otomatis.ambil() || this.sedangMulai) return;
    if (sekarang - this.percobaanOtomatis < this.jedaOtomatis) return; // jangan menghujani server bila gagal (mis. belum login)
    this.percobaanOtomatis = sekarang;
    this.sedangMulai = true;
    this.mulai().catch(() => null).then(() => { this.sedangMulai = false; });
  }

  antri(pesan, segera = false) {
    this.diinginkan = pesan;
    // Sama dengan yang terakhir sampai ke server dan tidak ada kiriman lain di jalan: tidak perlu kirim.
    if (!this.mengirim && JSON.stringify(pesan) === this.terakhirDikirim) {
      this.tertunda = null;
      return;
    }
    this.tertunda = pesan;
    clearTimeout(this.timer);
    if (segera) this.kirimTertunda();
    else this.timer = setTimeout(() => this.kirimTertunda(), this.jeda);
  }

  async kirimTertunda() {
    if (this.mengirim) return;
    this.mengirim = true;
    try {
      let sudahBuatUlang = false;
      while (this.tertunda && this.aktif) {
        const pesan = this.tertunda;
        this.tertunda = null;
        const { status } = await this.panggil('POST', `/layar-pelanggan/${this.kode}/pesan`, pesan);
        if (status === 200) {
          this.terakhirDikirim = JSON.stringify(pesan);
          // Selama kiriman ini di jalan keadaan kasir bisa sudah berubah (mis. +1 lalu -1): kirim keadaan terbaru bila beda.
          if (!this.tertunda && this.diinginkan && JSON.stringify(this.diinginkan) !== this.terakhirDikirim) this.tertunda = this.diinginkan;
        } else if ((status === 404 || status === 403) && !sudahBuatUlang) {
          // 404: server dimulai ulang (sesi hilang). 403: sesi milik akun lain. Buat sesi baru (kode berganti) lalu kirim lagi.
          sudahBuatUlang = true;
          this.ubahKode(null);
          if (await this.mulai()) this.tertunda = this.tertunda || pesan;
        }
        // selain itu (server tidak terjangkau, 401, 5xx): pesan berikutnya dikirim saat ada perubahan
      }
    } finally {
      this.mengirim = false;
    }
  }
}

// X-Perangkat-Id: server memisahkan sesi layar per akun DAN per perangkat (web & aplikasi dengan akun sama tidak berbagi layar).
const klien = axios.create({ withCredentials: true, validateStatus: () => true, timeout: 10000, headers: { 'X-Perangkat-Id': idPerangkat() } });
const alamatApi = () => `${window.location.protocol === 'https:' ? 'https' : 'http'}://${apiHost()}`;

const panggilServer = async (metode, path, data) => {
  try {
    const r = await klien.request({ method: metode, url: `${alamatApi()}${path}`, data });
    return { status: r.status, data: r.data };
  } catch (e) {
    return { status: 0, data: null }; // jaringan putus / timeout
  }
};

const KUNCI_AKTIF = 'layarPelangganAktif';
const penyimpan = {
  ambil: () => { try { return window.localStorage.getItem(KUNCI_AKTIF) === '1'; } catch (e) { return false; } },
  simpan: (v) => { try { if (v) window.localStorage.setItem(KUNCI_AKTIF, '1'); else window.localStorage.removeItem(KUNCI_AKTIF); } catch (e) { /* penyimpanan diblokir */ } },
};

export const layarServer = new PengirimLayar({ panggil: panggilServer, otomatis: penyimpan });

const urlLayar = (kode) => `${alamatLayar(alamatApi(), process.env.REACT_APP_LAYAR_URL)}?kode=${encodeURIComponent(kode)}${layarServer.token ? `&token=${encodeURIComponent(layarServer.token)}` : ''}&ambil=1`;

let jendela = null;
// Kode berubah (server restart / akun berganti): jendela layar yang masih terbuka diarahkan ke kode baru.
layarServer.saatKodeBerubah = (kode) => {
  if (kode && jendela && !jendela.closed) {
    try { jendela.location.href = urlLayar(kode); } catch (e) { /* jendela sudah ditutup */ }
  }
};

/** Klik "Layar Pelanggan": buat sesi lalu buka /layar dengan kode terisi otomatis. Dipanggil dari klik pengguna. */
export const bukaLayarPelanggan = async () => {
  // Jendela dibuka SEKARANG (masih dalam klik pengguna, supaya tidak diblokir pop-up) lalu diarahkan setelah sesi siap.
  const w = window.open('', 'layar-pelanggan', 'width=1100,height=760');
  const kode = await layarServer.mulai();
  if (!kode) {
    if (w) w.close();
    window.alert('Layar pelanggan butuh koneksi ke server. Coba lagi setelah terhubung.');
    return false;
  }
  if (!w) {
    window.alert(`Pop-up diblokir browser. Izinkan pop-up untuk situs ini, atau buka ${urlLayar(kode)} di perangkat lain.`);
    return false;
  }
  jendela = w;
  w.location.href = urlLayar(kode);
  w.focus();
  return true;
};
